/**
 * SVGZ asset loader with global URL-keyed deduplication cache.
 *
 * Fetches SVGZ files, decompresses them via the native DecompressionStream API,
 * and caches the resulting SVG text globally — each URL is fetched at most once
 * per page load regardless of how many overlays reference it.
 *
 * @module mapLibrary/svgzLoader
 */

// Global cache: URL → Promise<string|null> (decompressed SVG text, or null on fetch failure)
const svgLayerTextCache = new Map();

/**
 * Fetches a SVGZ URL, decompresses it via the native DecompressionStream API,
 * and returns the SVG text. Results are cached globally — each URL is fetched at most once.
 *
 */
export default function fetchSvgzLayerText(url) {
  if (!svgLayerTextCache.has(url)) {
    const loadPromise = (async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          // Non-OK responses should be retriable on later calls.
          svgLayerTextCache.delete(url);
          return null;
        }

        // Prefer the simple path first. This matches prior behavior and works when
        // the browser/server already handles content decompression.
        const svgText = await response.text();
        if (/<svg[\s>]/i.test(svgText)) {
          return svgText;
        }

        const canDecompress = typeof window !== 'undefined' &&
          typeof window.DecompressionStream === 'function';
        if (!canDecompress) {
          return null;
        }

        // Fallback: fetch again and attempt explicit gzip decompression for SVGZ payloads.
        const compressedResponse = await fetch(url);
        if (!compressedResponse.ok || !compressedResponse.body) {
          svgLayerTextCache.delete(url);
          return null;
        }

        const decompressedStream = compressedResponse.body.pipeThrough(
          new window.DecompressionStream('gzip')
        );
        return new Response(decompressedStream).text();
      } catch {
        // Network/CORS failures should not permanently poison the cache.
        svgLayerTextCache.delete(url);
        return null;
      }
    })();
    svgLayerTextCache.set(url, loadPromise);
  }
  return svgLayerTextCache.get(url);
}
